import { useDispatch, useSelector } from 'react-redux';
import { setCustomContext, setPopupStatus } from '../popup/PopupActions';
import useGlifeCommunication from './useGlifeCommunication';
import { useGlifeOneTimeToken } from './useGlifeOneTimeToken';
import { PopupStatus } from '../popup/PopupReducer';
import { AppState } from '../RootReducer';
import { useCallback, useEffect, useRef } from 'react';
import { getUserUid } from '../selectors/user-selectors';

export enum OneTimeTokenStatus {
	LOADING = 'loading',
	SUCCESS = 'success',
	FAILED = 'failed',
}

const ROOT_DOMAIN = window.location.origin;

export const useGlifeOpenExternalLink = () => {
	const dispatch = useDispatch();
    const { getOneTimeToken } = useGlifeOneTimeToken();
	const { handleOpenExternalLink } = useGlifeCommunication();
	const { hasUserClickedConfirm } = useSelector((state: AppState) => state.popup?.customContext);
	const userUid = useSelector(getUserUid);

	const urlRef = useRef<string | null>(null);
	const tokenRef = useRef<string | null>(null);

	const executeOpenExternalLink = useCallback((url: string, token: string) => {
		const externalLinkUrl = `${ROOT_DOMAIN}/glife/external-link?link=${encodeURIComponent(url)}&token=${token}&useruid=${userUid}`;
		handleOpenExternalLink(externalLinkUrl);
	}, [handleOpenExternalLink]);
	const openPopupDisclaimer = () => dispatch(setPopupStatus(PopupStatus.GLIFE_EXTERNAL_LINK_DISCLAIMER) as any)
	const resetPopupStatus = useCallback(() => dispatch(setPopupStatus(PopupStatus.HIDDEN) as any), [dispatch]);
	const setInitialCustomContext = useCallback(() => dispatch(setCustomContext({oneTimeTokenStatus: OneTimeTokenStatus.LOADING, hasUserClickedConfirm: false}) as any), [dispatch]);
	const openExternalLink = async (url: string) => {
		openPopupDisclaimer();
		setInitialCustomContext();
		const token = await getOneTimeToken();
		urlRef.current = url;
		tokenRef.current = token;

		if(!token) {
			dispatch(setCustomContext({oneTimeTokenStatus: OneTimeTokenStatus.FAILED}) as any);
			return console.error('useGlifeOpenExternalLink: token is null');
		}
		dispatch(setCustomContext({oneTimeTokenStatus: OneTimeTokenStatus.SUCCESS}) as any);
	};

	useEffect(() => {
		if(!hasUserClickedConfirm || !urlRef.current || !tokenRef.current) return;

		setInitialCustomContext();
		resetPopupStatus();
		executeOpenExternalLink(urlRef.current, tokenRef.current);
	}, [hasUserClickedConfirm, executeOpenExternalLink, resetPopupStatus, setInitialCustomContext]);

	return {
		openExternalLink,
	};
}